import React, { useState, useCallback } from 'react';
import { Heading, Text, Button, Flex } from '@vibe/core';
import { useReportStore } from '../../stores/reportStore';
import type { LeaveReportResponse } from '../../api/report.api';
import { createBookmark } from '../../api/report.api';
import LeaveReportExportButton from './LeaveReportExportButton';
import BookmarkDialog from './BookmarkDialog';

interface Props {
  data: LeaveReportResponse;
}

const LeaveReportHeader: React.FC<Props> = ({ data }) => {
  const { currentConfig, setView, setBookmarks, bookmarks } = useReportStore();
  const [showBookmarkDialog, setShowBookmarkDialog] = useState(false);
  const [bookmarking, setBookmarking] = useState(false);

  const handleBookmark = useCallback(
    async (name: string) => {
      if (!currentConfig) return;
      setBookmarking(true);
      try {
        const bookmark = await createBookmark({
          name,
          reportType: 'leave_report',
          config: currentConfig,
        });
        setBookmarks([...bookmarks, bookmark]);
        setShowBookmarkDialog(false);
      } catch (err) {
        console.error('Failed to bookmark report:', err);
      } finally {
        setBookmarking(false);
      }
    },
    [currentConfig, bookmarks, setBookmarks],
  );

  const formatPeriodDate = (dateStr: string): string => {
    const date = new Date(dateStr + 'T00:00:00');
    return date.toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    });
  };

  return (
    <div className="rp-report-header">
      <Flex
        justify={Flex.justify.SPACE_BETWEEN}
        align={Flex.align.CENTER}
      >
        <div>
          <Heading type={Heading.types.H2}>Leave Report</Heading>
          <Text type={Text.types.TEXT2} style={{ color: '#676879', marginTop: 4 }}>
            Period: {formatPeriodDate(data.period.startDate)} &ndash;{' '}
            {formatPeriodDate(data.period.endDate)}
          </Text>
          <Flex gap={Flex.gaps.MEDIUM} style={{ marginTop: 4 }}>
            <Text type={Text.types.TEXT2} style={{ color: '#676879' }}>
              {data.summary.totalUsers} users
            </Text>
            <Text type={Text.types.TEXT2} style={{ color: '#676879' }}>
              {data.summary.totalLeaveDays} total leave days
            </Text>
            <Text type={Text.types.TEXT2} style={{ color: '#676879' }}>
              {data.summary.averageLeaveDaysPerUser} avg days/user
            </Text>
          </Flex>
        </div>
        <Flex gap={Flex.gaps.SMALL}>
          <LeaveReportExportButton data={data} />
          <Button
            kind={Button.kinds.SECONDARY}
            size={Button.sizes.SMALL}
            onClick={() => setShowBookmarkDialog(true)}
          >
            Bookmark
          </Button>
          <Button
            kind={Button.kinds.SECONDARY}
            size={Button.sizes.SMALL}
            onClick={() => setView('config')}
          >
            Customize Report
          </Button>
          <Button
            kind={Button.kinds.TERTIARY}
            size={Button.sizes.SMALL}
            onClick={() => setView('gallery')}
          >
            Back to Reports
          </Button>
        </Flex>
      </Flex>

      {showBookmarkDialog && (
        <BookmarkDialog
          saving={bookmarking}
          onSave={handleBookmark}
          onClose={() => setShowBookmarkDialog(false)}
        />
      )}
    </div>
  );
};

export default LeaveReportHeader;
